import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { HeroElementDataType } from '@/components/sections/builder/elements/body/hero/types';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import InputLink from '@/components/sections/builder/settings/input-link';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';

const IMAGE_HEIGHT = [
  { value: 'adapt', label: 'Adapt to first Image' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as HeroElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <div className='mb-4 font-bold'>Layout</div>
      <InputTab
        grid={2}
        defaultValue={data.layout}
        options={[
          { label: 'Full Width', value: 'full' },
          { label: 'Grid', value: 'grid' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { layout: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Content Align</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Image</div>
      <ImageSetting
        value={data?.image}
        onChange={(v) => builder.setElementData(element.id, { image: v })}
      />
      <div className='mb-2 mt-4'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setElementData(element.id, {
            mobileImg: val,
          });
        }}
      />
      <InputSlider
        label='Device under this size use mobile image'
        value={data.mobileMinWidth || 0}
        max={1440}
        min={0}
        rightContent={<div>px</div>}
        onChange={(val) =>
          builder.setElementData(element.id, {
            mobileMinWidth: val,
          })
        }
      />
      <div className='mb-2 mt-2 text-xs'>Image Position</div>
      <InputTab
        grid={4}
        defaultValue={data.imagePosition}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Right', value: 'right' },
          { label: 'Top', value: 'top' },
          { label: 'Bottom', value: 'bottom' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { imagePosition: v })}
      />
      <div className='mb-2 mt-2'>Image Height</div>
      <Select
        name='imageHeight'
        value={data.imageHeight}
        onValueChange={(val) => {
          builder.setElementData(element.id, {
            imageHeight: val as string,
          });
        }}
      >
        <SelectTrigger className='py-5'>
          <SelectValue placeholder='Select' />
        </SelectTrigger>
        <SelectContent>
          {IMAGE_HEIGHT.map((obj) => (
            <SelectItem key={obj.value} value={obj.value}>
              {obj.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className='mb-2 mt-2'>Image Link</div>
      <InputLink
        name='url_path_img'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setElementData(element.id, {
            imageUrl: value,
          });
        }}
        value={data.imageUrl as string}
      />
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <div className='mb-2 mt-4 font-bold'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-4 mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>

      <BackgroundSetting
        value={data.background}
        isShowBackgroundLayout={false}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      {data?.background?.type !== 'image' && (
        <>
          <div className='mb-4 mt-4 font-bold'>Overlay</div>
          <ColorPicker
            color={data.overlay}
            inputColor={data.overlay}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) =>
              builder.setElementData(element.id, { overlay: e.target.value })
            }
            onChangePercentage={(val) =>
              builder.setElementData(element.id, { overlayPercentage: val })
            }
          />
        </>
      )}
    </div>
  );
}
